import React from "react";
import { cn } from "@/lib/utils";
import { Target, CheckCircle2, AlertCircle } from "lucide-react";

interface RadialTargetGaugeProps {
  achieved: number;
  target: number;
  label?: string;
  sublabel?: string;
  size?: number;
  strokeWidth?: number;
  formatMoney?: (v: number) => string;
  className?: string;
}

export function RadialTargetGauge({
  achieved,
  target,
  label = "Quota Progress",
  sublabel,
  size = 170,
  strokeWidth = 14,
  formatMoney = (v) => `₹${v.toLocaleString("en-IN")}`,
  className,
}: RadialTargetGaugeProps) {
  if (!target || target <= 0) {
    return (
      <div className={cn("flex flex-col items-center justify-center p-4 text-center text-xs text-muted-foreground", className)}>
        <Target className="size-8 text-muted-foreground/40 mb-1" />
        <span className="font-semibold text-foreground">No Target Configured</span>
        <span className="text-[11px]">Set a monthly target to track quota pacing</span>
      </div>
    );
  }

  const pct = Math.max(0, (achieved / target) * 100);
  const clampedPct = Math.min(100, pct);

  // Status color logic
  let strokeColor = "#f43f5e"; // rose-500
  let badgeText = "Behind Pace";
  let badgeBg = "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300";

  if (pct >= 100) {
    strokeColor = "#2563eb"; // blue-600 (Exceeded)
    badgeText = "Goal Exceeded!";
    badgeBg = "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300";
  } else if (pct >= 80) {
    strokeColor = "#10b981"; // emerald-500
    badgeText = "On Track";
    badgeBg = "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
  } else if (pct >= 50) {
    strokeColor = "#f59e0b"; // amber-500
    badgeText = "In Progress";
    badgeBg = "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300";
  }

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // Use a 240-degree open gauge arc
  const arcLength = circumference * (240 / 360);
  const strokeDashoffset = arcLength - (clampedPct / 100) * arcLength;

  return (
    <div className={cn("flex flex-col items-center justify-center", className)}>
      <div className="relative flex items-center justify-center" style={{ width: size, height: size * 0.9 }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="rotate-[150deg] overflow-visible"
        >
          {/* Background Track Arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeLinecap="round"
            className="text-muted/60"
          />

          {/* Progress Arc */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={`${arcLength} ${circumference}`}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-700 ease-out"
          />
        </svg>

        {/* Center Percentage & Value */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center pt-2">
          <span className="text-2xl sm:text-3xl font-black text-foreground tracking-tight tabular-nums">
            {pct.toFixed(0)}%
          </span>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold mt-1 shadow-2xs ${badgeBg}`}>
            {badgeText}
          </span>
        </div>
      </div>

      {/* Target & Achieved Metrics Footer */}
      <div className="flex items-center justify-between gap-4 w-full pt-1 border-t border-border/60 text-xs tabular-nums">
        <div className="flex flex-col">
          <span className="text-[10px] uppercase font-bold text-muted-foreground">Achieved</span>
          <span className="font-extrabold text-foreground">{formatMoney(achieved)}</span>
        </div>
        <div className="flex flex-col text-right">
          <span className="text-[10px] uppercase font-bold text-muted-foreground">Target</span>
          <span className="font-semibold text-muted-foreground">{formatMoney(target)}</span>
        </div>
      </div>
    </div>
  );
}

